<template>
  <Teleport to="body">
    <div v-if="state.open" class="cd-backdrop" @mousedown.self="cancel">
      <div ref="dialog" class="cd-dialog" role="alertdialog" aria-modal="true"
        aria-labelledby="cd-title" :aria-describedby="state.message ? 'cd-message' : null"
        tabindex="-1" @keydown.esc.stop="cancel" @keydown.tab="trapFocus">
        <h2 id="cd-title" class="cd-title">{{ state.title }}</h2>
        <p v-if="state.message" id="cd-message" class="cd-message">{{ state.message }}</p>
        <div class="cd-actions">
          <button ref="cancelBtn" type="button" class="cd-btn cd-btn--cancel" @click="cancel">
            {{ state.cancelLabel }}
          </button>
          <button ref="confirmBtn" type="button" class="cd-btn"
            :class="state.destructive ? 'cd-btn--destructive' : 'cd-btn--primary'" @click="confirm">
            {{ state.confirmLabel }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { nextTick } from 'vue';
import { confirmState, settleConfirm } from '../utils/confirm';

export default {
  name: 'ConfirmDialog',
  data() {
    return { state: confirmState, returnFocus: null };
  },
  watch: {
    'state.open'(open) {
      if (open) {
        this.returnFocus = document.activeElement;
        // Focus the dialog, not a button: neither choice is the default, so
        // Enter can't confirm by accident (alerts.md › Buttons).
        nextTick(() => this.$refs.dialog && this.$refs.dialog.focus());
      } else if (this.returnFocus && this.returnFocus.focus) {
        this.returnFocus.focus();
        this.returnFocus = null;
      }
    },
  },
  methods: {
    confirm() {
      settleConfirm(true);
    },
    cancel() {
      settleConfirm(false);
    },
    trapFocus(e) {
      const first = this.$refs.cancelBtn;
      const last = this.$refs.confirmBtn;
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.$refs.dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style scoped>
.cd-backdrop {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(11, 18, 32, 0.45);
}

.cd-dialog {
  width: min(420px, 100%);
  padding: 24px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 20px 48px rgba(11, 18, 32, 0.25);
  text-align: left;
  outline: none;
}

.cd-title {
  margin: 0 0 8px;
  font-family: var(--font-sans);
  font-size: 1.0625rem;
  font-weight: 600;
  line-height: 1.4;
  color: var(--text-primary, #0b1220);
}

.cd-message {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--text-secondary, #475467);
}

.cd-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

.cd-btn {
  min-height: 36px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-family: var(--font-sans);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.cd-btn--cancel {
  background: #fff;
  border-color: #d0d5dd;
  color: #344054;
}

.cd-btn--cancel:hover {
  background: #f2f4f7;
}

/* white on #0071C5 = 5.04:1 */
.cd-btn--primary {
  background: #0071c5;
  color: #fff;
}

.cd-btn--primary:hover {
  background: #005a9e;
}

/* white on #b42318 = 6.5:1 */
.cd-btn--destructive {
  background: #b42318;
  color: #fff;
}

.cd-btn--destructive:hover {
  background: #912018;
}

.cd-btn:focus-visible {
  outline: 2px solid #0071c5;
  outline-offset: 2px;
}
</style>
